import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";
import { Mail, Save, Send, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useApi } from "./useApi";

export function OrgEmailSettings({ token, user, t }) {
  var [orgEmailConfig, setOrgEmailConfig] = useState({ configured: false });
  var [orgEmailForm, setOrgEmailForm] = useState({ sender_email: "", sender_name: "Property Management", use_sendgrid: false, sendgrid_api_key: "", smtp_host: "smtp.gmail.com", smtp_port: 587, smtp_username: "", smtp_password: "", smtp_use_tls: true, notify_contract_expiring: true, notify_service_scheduled: true, contract_notify_days: [10, 1], service_notify_days: [10, 1] });
  var [savingOrgEmail, setSavingOrgEmail] = useState(false);
  var [testingOrgEmail, setTestingOrgEmail] = useState(false);
  var { fetchJson, postJson, putJson, fetchApi } = useApi(token);

  useEffect(function() { fetchOrgEmailConfig(); }, []);

  async function fetchOrgEmailConfig() {
    try {
      var data = await fetchJson("/api/org-email/config");
      setOrgEmailConfig(data);
      if (data.configured) {
        setOrgEmailForm(function(prev) { return { ...prev, sender_email: data.sender_email || "", sender_name: data.sender_name || "Property Management", use_sendgrid: data.use_sendgrid || false, notify_contract_expiring: data.notify_contract_expiring !== false, notify_service_scheduled: data.notify_service_scheduled !== false, contract_notify_days: data.contract_notify_days || [10, 1], service_notify_days: data.service_notify_days || [10, 1] }; });
      }
    } catch (err) {}
  }

  async function handleSaveOrgEmailConfig() {
    setSavingOrgEmail(true);
    try {
      var response = await postJson("/api/org-email/configure", orgEmailForm);
      if (response.ok) { toast.success(t("settings.orgEmailConfigured")); fetchOrgEmailConfig(); }
      else { var error = await response.json(); toast.error(error.detail || t("common.error")); }
    } catch (err) { toast.error(t("common.error")); }
    finally { setSavingOrgEmail(false); }
  }

  async function handleUpdateOrgEmailNotifications() {
    try {
      var response = await putJson("/api/org-email/config", { notify_contract_expiring: orgEmailForm.notify_contract_expiring, notify_service_scheduled: orgEmailForm.notify_service_scheduled, contract_notify_days: orgEmailForm.contract_notify_days, service_notify_days: orgEmailForm.service_notify_days, sender_name: orgEmailForm.sender_name });
      if (response.ok) toast.success(t("settings.notificationsUpdated"));
    } catch (err) { toast.error(t("common.error")); }
  }

  async function handleRemoveOrgEmailConfig() {
    try {
      var response = await fetchApi("/api/org-email/config", { method: "DELETE" });
      if (response.ok) { toast.success(t("settings.orgEmailRemoved")); setOrgEmailConfig({ configured: false }); }
    } catch (err) { toast.error(t("common.error")); }
  }

  async function handleTestOrgEmail() {
    setTestingOrgEmail(true);
    try {
      var response = await fetchApi("/api/org-email/test", { method: "POST" });
      if (response.ok) toast.success(t("settings.testEmailSent", { email: user?.email }));
      else { var error = await response.json(); toast.error(error.detail || t("settings.testEmailFailed")); }
    } catch (err) { toast.error(t("settings.testEmailFailed")); }
    finally { setTestingOrgEmail(false); }
  }

  return (
    <Card data-testid="org-email-settings">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Mail className="w-5 h-5 text-purple-500" />{t("settings.orgEmailTitle")}</CardTitle>
        <CardDescription>{t("settings.orgEmailDesc")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {orgEmailConfig.configured ? (
          <div className="space-y-6">
            <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <Check className="w-5 h-5 text-green-600" />
              <div>
                <p className="font-medium text-green-800 dark:text-green-300">{t("settings.orgEmailActive")}</p>
                <p className="text-sm text-green-600 dark:text-green-400">{t("settings.sendingFrom")}: <span className="font-semibold">{orgEmailConfig.sender_email}</span></p>
              </div>
            </div>
            <div className="space-y-4">
              <h4 className="font-medium">{t("settings.notificationTypes")}</h4>
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div><p className="font-medium">{t("settings.contractExpiringNotify")}</p><p className="text-sm text-muted-foreground">{t("settings.contractExpiringDesc")}</p></div>
                <Switch checked={orgEmailForm.notify_contract_expiring} onCheckedChange={function(v) { setOrgEmailForm({ ...orgEmailForm, notify_contract_expiring: v }); }} />
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div><p className="font-medium">{t("settings.serviceScheduledNotify")}</p><p className="text-sm text-muted-foreground">{t("settings.serviceScheduledDesc")}</p></div>
                <Switch checked={orgEmailForm.notify_service_scheduled} onCheckedChange={function(v) { setOrgEmailForm({ ...orgEmailForm, notify_service_scheduled: v }); }} />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label>{t("settings.contractNotifyDays")}</Label>
                <p className="text-xs text-muted-foreground mb-2">{t("settings.notifyDaysHelp")}</p>
                <Input value={orgEmailForm.contract_notify_days.join(", ")} onChange={function(e) { var days = e.target.value.split(",").map(function(d) { return parseInt(d.trim()); }).filter(function(d) { return !isNaN(d) && d > 0; }); setOrgEmailForm({ ...orgEmailForm, contract_notify_days: days.length > 0 ? days : [10, 1] }); }} placeholder="10, 1" />
              </div>
              <div>
                <Label>{t("settings.serviceNotifyDays")}</Label>
                <p className="text-xs text-muted-foreground mb-2">{t("settings.notifyDaysHelp")}</p>
                <Input value={orgEmailForm.service_notify_days.join(", ")} onChange={function(e) { var days = e.target.value.split(",").map(function(d) { return parseInt(d.trim()); }).filter(function(d) { return !isNaN(d) && d >= 0; }); setOrgEmailForm({ ...orgEmailForm, service_notify_days: days.length > 0 ? days : [10, 1] }); }} placeholder="10, 1" />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={handleUpdateOrgEmailNotifications}><Save className="w-4 h-4 mr-2" />{t("settings.saveNotifications")}</Button>
              <Button variant="outline" onClick={handleTestOrgEmail} disabled={testingOrgEmail}>{testingOrgEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}{t("settings.sendTestEmail")}</Button>
              <Button variant="destructive" onClick={handleRemoveOrgEmailConfig}><X className="w-4 h-4 mr-2" />{t("settings.removeEmailConfig")}</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label>{t("settings.emailProvider")}</Label>
              <Select value={orgEmailForm.use_sendgrid ? "sendgrid" : "smtp"} onValueChange={function(v) { setOrgEmailForm({ ...orgEmailForm, use_sendgrid: v === "sendgrid" }); }}>
                <SelectTrigger data-testid="email-provider-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="smtp">SMTP (Gmail, Outlook, etc.)</SelectItem><SelectItem value="sendgrid">SendGrid</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><Label>{t("settings.senderEmail")}</Label><Input type="email" value={orgEmailForm.sender_email} onChange={function(e) { setOrgEmailForm({ ...orgEmailForm, sender_email: e.target.value }); }} placeholder="noreply@yourcompany.com" /></div>
              <div><Label>{t("settings.senderName")}</Label><Input value={orgEmailForm.sender_name} onChange={function(e) { setOrgEmailForm({ ...orgEmailForm, sender_name: e.target.value }); }} placeholder="Property Management" /></div>
            </div>
            {orgEmailForm.use_sendgrid ? (
              <div className="space-y-4 p-4 border rounded-lg">
                <h4 className="font-medium">{t("settings.sendgridCredentials")}</h4>
                <div><Label>{t("settings.sendgridApiKey")}</Label><Input type="password" value={orgEmailForm.sendgrid_api_key} onChange={function(e) { setOrgEmailForm({ ...orgEmailForm, sendgrid_api_key: e.target.value }); }} placeholder="SG.xxxxxxxxxx" /></div>
              </div>
            ) : (
              <div className="space-y-4 p-4 border rounded-lg">
                <h4 className="font-medium">{t("settings.smtpCredentials")}</h4>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div><Label>{t("settings.smtpHost")}</Label><Input value={orgEmailForm.smtp_host} onChange={function(e) { setOrgEmailForm({ ...orgEmailForm, smtp_host: e.target.value }); }} placeholder="smtp.gmail.com" /></div>
                  <div><Label>{t("settings.smtpPort")}</Label><Input type="number" value={orgEmailForm.smtp_port} onChange={function(e) { setOrgEmailForm({ ...orgEmailForm, smtp_port: parseInt(e.target.value) || 587 }); }} placeholder="587" /></div>
                  <div><Label>{t("settings.smtpUsername")}</Label><Input value={orgEmailForm.smtp_username} onChange={function(e) { setOrgEmailForm({ ...orgEmailForm, smtp_username: e.target.value }); }} placeholder="your-email@gmail.com" /></div>
                  <div><Label>{t("settings.smtpPassword")}</Label><Input type="password" value={orgEmailForm.smtp_password} onChange={function(e) { setOrgEmailForm({ ...orgEmailForm, smtp_password: e.target.value }); }} placeholder="App password" /></div>
                </div>
                <div className="flex items-center gap-2"><Switch checked={orgEmailForm.smtp_use_tls} onCheckedChange={function(v) { setOrgEmailForm({ ...orgEmailForm, smtp_use_tls: v }); }} /><Label>{t("settings.useTls")}</Label></div>
              </div>
            )}
            <Button onClick={handleSaveOrgEmailConfig} disabled={savingOrgEmail}>{savingOrgEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}{t("settings.saveEmailConfig")}</Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
